import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { fadeInOut } from 'src/app/shared/services/app.animation';
import { AdhocInvoiceService } from '../shared/adhoc-invoice.service';
import { PaymentGatewayService } from 'src/app/shared/services/payment-gateway.service';
import {
  PaymentContext,
  PaymentFlowType,
} from 'src/app/shared/interfaces/payment-gateway.interface';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-adhoc-payment-options',
  templateUrl: './adhoc-payment-options.component.html',
  styleUrls: ['./adhoc-payment-options.component.scss'],
  animations: [fadeInOut],
})
export class AdhocPaymentOptionsComponent implements OnInit, OnDestroy {
  activeTab: 'cvv' | 'creditCard' = 'cvv';
  gatewayConfig: any = null;
  paymentFlowType: PaymentFlowType = PaymentFlowType.TRADITIONAL;
  readonly paymentFlowTypeEnum = PaymentFlowType;
  readonly paymentContext = PaymentContext.ADHOC_INVOICE;
  isGatewayLoading = false;
  invoiceEmittedData: any = null;
  braintreeReloadToken = 0;

  private invoiceSubscription: Subscription | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private adhocInvoiceService: AdhocInvoiceService,
    private paymentGatewayService: PaymentGatewayService
  ) {}

  private showPaymentModal(type: 'success' | 'cancel', message: string): void {
    const isSuccess = type === 'success';
    Swal.fire({
      icon: isSuccess ? 'success' : 'error',
      title: isSuccess ? 'Success' : 'Cancelled',
      text: message,
      confirmButtonText: 'OK',
    });
  }

  private extractErrorMessage(source: any, fallback: string): string {
    if (!source) return fallback;
    if (typeof source === 'string') return source;
    if (typeof source?.error === 'string') return source.error;
    if (typeof source?.error?.message === 'string') return source.error.message;
    if (typeof source?.error?.error === 'string') return source.error.error;
    if (typeof source?.error?.error?.message === 'string') return source.error.error.message;
    if (typeof source?.message === 'string') return source.message;
    if (typeof source?.msg === 'string') return source.msg;
    return fallback;
  }

  private triggerBraintreeReload(): void {
    this.braintreeReloadToken += 1;
  }

  async ngOnInit(): Promise<void> {
    // invoiceObservable$ is a BehaviorSubject — subscribing sets invoiceEmittedData synchronously
    this.invoiceSubscription = this.adhocInvoiceService.invoiceObservable$.subscribe(
      (data) => {
        this.invoiceEmittedData = data;
      }
    );

    await this.loadGatewayConfig();  // invoiceEmittedData is already set above
  }

  ngOnDestroy(): void {
    this.invoiceSubscription?.unsubscribe();
  }

  get is3dsEnabled(): boolean {
    return this.gatewayConfig?.actions?.['3ds_secure'] ?? false;
  }

  switchTab(tab: 'cvv' | 'creditCard') {
    this.activeTab = tab;
  }

  closeModal() {
    this.activeModal.dismiss('Modal closed');
  }

  handlePaymentSuccess() {
    this.adhocInvoiceService.updatePaymentStatus(true);
    this.activeModal.close('Payment successful');
  }

  async onGatewayBraintreeNonce(nonce: string): Promise<void> {
    if (!nonce) {
      return;
    }

    await this.processGatewayPayment(nonce);
  }

  onGatewayBraintreeError(errorMessage: string): void {
    this.showPaymentModal(
      'cancel',
      errorMessage || 'Unable to initialize secure payment form.'
    );
    if (this.paymentFlowType === PaymentFlowType.BRAINTREE) {
      this.triggerBraintreeReload();
    }
  }

  async redirectToStripeCheckout(): Promise<void> {
    if (!this.hasInvoiceContext()) {
      this.showPaymentModal(
        'cancel',
        'Invoice details are not available for payment.'
      );
      return;
    }

    try {
      const invoiceType = this.getInvoiceApiType();
      const billingAddress = this.getBillingAddress();
      const context = {
        customerId: String(this.invoiceEmittedData?.data?.customer_id || ''),
        amount: Number(this.invoiceEmittedData?.data?.total_amount || 0),
        context: PaymentContext.ADHOC_INVOICE,
        billingAddress,
        customerZipCodeId: billingAddress?.zipCode || null,
        returnUrl: window.location.href,
        metadata: {
          invoiceId: this.invoiceEmittedData?.data?.id || null,
          invoiceType,
          advancePlanId: this.invoiceEmittedData?.data?.advancePlanId || null,
        },
      };

      localStorage.setItem('stripePaymentContext', JSON.stringify(context));
      this.activeModal.dismiss('stripe-redirect');  // close modal before browser navigates
      await this.paymentGatewayService.redirectToStripeCheckout({
        customerId: String(this.invoiceEmittedData?.data?.customer_id || ''),
        amount: Number(this.invoiceEmittedData?.data?.total_amount || 0),
        context: PaymentContext.ADHOC_INVOICE,
        returnUrl: window.location.href,
        metadata: context.metadata,
      });
    } catch (error: any) {
      this.showPaymentModal(
        'cancel',
        error?.message || 'Failed to redirect to Stripe checkout.'
      );
    }
  }

  private async loadGatewayConfig(): Promise<void> {
    this.isGatewayLoading = true;
    this.gatewayConfig = null;
    this.paymentFlowType = PaymentFlowType.TRADITIONAL;

    try {
      const customerId = String(this.invoiceEmittedData?.data?.customer_id || '');
      const gatewayData = await this.paymentGatewayService.detectGateway(customerId);

      if (gatewayData) {
        this.gatewayConfig = gatewayData;
        this.paymentFlowType =
          this.paymentGatewayService.determinePaymentFlow(gatewayData);

        // Auto-redirect to Stripe — do not show card tabs at all
        if (this.paymentFlowType === PaymentFlowType.STRIPE_REDIRECT) {
          await this.redirectToStripeCheckout();
        }
      }
    } catch (error) {
      console.error('Failed to load adhoc payment gateway config:', error);
    } finally {
      this.isGatewayLoading = false;
    }
  }

  private hasInvoiceContext(): boolean {
    return !!(
      this.invoiceEmittedData?.data?.customer_id &&
      this.invoiceEmittedData?.data?.id
    );
  }

  private getInvoiceApiType(): string {
    // Valid invoice types for payment API
    const VALID_INVOICE_TYPES = ['PlanChange', 'Renewal', 'EquipmentPurchase', 'ESIM'];
    
    // Map display names to API values
    const invoiceTypeMapping: { [key: string]: string } = {
      'Plan Change': 'PlanChange',
      'PlanChange': 'PlanChange',
      'planChange': 'PlanChange',
      'Plan Renewal': 'Renewal',
      'PlanRenewal': 'Renewal',
      'Renewal': 'Renewal',
      'renewal': 'Renewal',
      'DevicePurchase': 'EquipmentPurchase',
      'Equipment': 'EquipmentPurchase',
      'EquipmentPurchase': 'EquipmentPurchase',
      'equipmentPurchase': 'EquipmentPurchase',
      'ESM': 'ESIM',
      'ESIM': 'ESIM',
      'esim': 'ESIM'
    };

    const rawType = this.invoiceEmittedData?.invoiceType || '';
    const mappedType = invoiceTypeMapping[rawType] || rawType;

    // Validate the mapped type
    if (!VALID_INVOICE_TYPES.includes(mappedType)) {
      console.warn(`[Payment] Invalid invoiceType "${rawType}" mapped to "${mappedType}". Defaulting to "Renewal".`);
      return 'Renewal';
    }

    return mappedType;
  }

  private getBillingAddress(): any {
    const mailingAddress =
      this.invoiceEmittedData?.customerData?.personalInfo?.mailingAddress?.value;

    return {
      address1: mailingAddress?.address1 || '',
      address2: mailingAddress?.address2 || '',
      city: mailingAddress?.city || '',
      state: mailingAddress?.state || '',
      zipCode: mailingAddress?.zipCode || '',
    };
  }

  private async processGatewayPayment(paymentMethodToken: string): Promise<void> {
    if (!this.hasInvoiceContext()) {
      this.showPaymentModal(
        'cancel',
        'Invoice details are not available for payment.'
      );
      return;
    }

    try {
      const payload: any = {
        customerId: this.invoiceEmittedData?.data?.customer_id,
        invoiceId: this.invoiceEmittedData?.data?.id || null,
        invoiceType: this.getInvoiceApiType(),
        chargeFrom: 'card',
        paymentMethodToken,
        advancePlanId: this.invoiceEmittedData?.data?.advancePlanId || null,
      };

      const response: any =
        await this.adhocInvoiceService.doAdhocInvoicePayment(payload);

      if (response?.Status) {
        this.handlePaymentSuccess();
      } else {
        this.showPaymentModal(
          'cancel',
          this.extractErrorMessage(response, 'Invoice payment failed.')
        );
      }
    } catch (error: any) {
      this.showPaymentModal(
        'cancel',
        this.extractErrorMessage(error, 'Invoice payment failed.')
      );
    } finally {
      if (this.paymentFlowType === PaymentFlowType.BRAINTREE) {
        this.triggerBraintreeReload();
      }
    }
  }
}
